import axios from "axios";
import apiUrls from "src/api";

export interface RagAgent {
  id: number;
  organization_id: number;
  created_by: number;
  name: string;
  description: string | null;
  system_prompt: string | null;
  llm_model: string;
  temperature: number;
  icon: string;
  status: string;
  is_shared?: boolean;
  created_at: string;
  updated_at: string;
  document_count?: number;
  total_chunks?: number;
}

export interface RagDocument {
  id: number;
  agent_id: number | null;
  organization_id: number;
  uploaded_by: number;
  filename: string;
  s3_key: string;
  mime_type: string;
  size_bytes: number;
  chunk_count: number;
  total_chunks_expected: number;
  status: "processing" | "ready" | "error";
  error_message: string | null;
  created_at: string;
  updated_at: string;
  agents?: Array<{ id: number; name: string }>;
  /** Set by upload responses when the server matched an existing document by
   *  content hash and attached it instead of ingesting a new copy. */
  deduplicated?: boolean;
}

export interface RagSource {
  filename: string;
  chunkIndex: number;
  preview: string;
  /**
   * Chunk provenance for office formats: which sheet or which slide the text
   * came from. Absent for PDF, TXT, DOCX and for every chunk indexed before
   * provenance shipped, so consumers must treat it as optional.
   */
  metadata?: { sheet?: string; slide?: number };
}

// --- Agent CRUD ---

export async function fetchAgents(): Promise<RagAgent[]> {
  const response = await axios.get(apiUrls.rag.agents);
  return response.data;
}

export async function fetchAgent(agentId: number): Promise<RagAgent> {
  const response = await axios.get(`${apiUrls.rag.agents}/${agentId}`);
  return response.data;
}

export async function createAgent(data: {
  name: string;
  description?: string;
  system_prompt?: string;
  llm_model?: string;
  temperature?: number;
  icon?: string;
}): Promise<RagAgent> {
  const response = await axios.post(apiUrls.rag.agents, data);
  return response.data;
}

export async function updateAgent(
  agentId: number,
  data: Partial<{
    name: string;
    description: string;
    system_prompt: string;
    llm_model: string;
    temperature: number;
    icon: string;
    status: string;
  }>,
): Promise<RagAgent> {
  const response = await axios.put(`${apiUrls.rag.agents}/${agentId}`, data);
  return response.data;
}

export async function deleteAgent(agentId: number): Promise<void> {
  await axios.delete(`${apiUrls.rag.agents}/${agentId}`);
}

// --- Document Management ---

export async function fetchDocuments(agentId: number): Promise<RagDocument[]> {
  const response = await axios.get(`${apiUrls.rag.agents}/${agentId}/documents`);
  return response.data;
}

export async function uploadDocument(agentId: number, file: File): Promise<RagDocument> {
  const jwt = localStorage.getItem("wixar.auth.jwt");
  const formData = new FormData();
  formData.append("file", file);

  // Use fetch instead of axios to bypass the request interceptor that wraps body in { data: ... }
  const response = await fetch(`${apiUrls.rag.agents}/${agentId}/documents`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(err.error?.message || "Upload failed");
  }

  return response.json();
}

export async function deleteDocument(docId: number): Promise<void> {
  await axios.delete(`${apiUrls.rag.documents}/${docId}`);
}

export async function retryDocument(docId: number): Promise<RagDocument> {
  const response = await axios.post(`${apiUrls.rag.documents}/${docId}/retry`);
  return response.data;
}

export interface DocumentStatus {
  id: number;
  status: "processing" | "ready" | "error";
  chunk_count: number;
  total_chunks_expected: number;
  error_message: string | null;
}

export async function fetchDocumentStatus(docId: number): Promise<DocumentStatus> {
  const response = await axios.get(`${apiUrls.rag.documents}/${docId}/status`);
  return response.data;
}

// --- Org-level Document Management ---

export async function fetchOrgDocuments(): Promise<RagDocument[]> {
  const response = await axios.get(apiUrls.rag.documents);
  return response.data;
}

export interface RagStorageUsage {
  /** Remaining org-wide media storage in bytes, null when unknown */
  availableBytes: number | null;
  /** Total org-wide media storage in bytes, null when unknown */
  includedBytes: number | null;
}

export async function fetchStorageUsage(): Promise<RagStorageUsage> {
  const response = await axios.get(apiUrls.rag.storageUsage);
  return response.data;
}

export async function uploadOrgDocument(file: File): Promise<RagDocument> {
  const jwt = localStorage.getItem("wixar.auth.jwt");
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(apiUrls.rag.documents, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(err.error?.message || "Upload failed");
  }

  return response.json();
}

export async function assignDocumentToAgent(docId: number, agentId: number): Promise<void> {
  await axios.post(`${apiUrls.rag.documents}/${docId}/agents/${agentId}`);
}

export async function unassignDocumentFromAgent(docId: number, agentId: number): Promise<void> {
  await axios.delete(`${apiUrls.rag.documents}/${docId}/agents/${agentId}`);
}

// --- Conversation History ---

export interface RagConversationMessage {
  role: "user" | "assistant";
  text: string;
  sources?: RagSource[];
}

/** List-row shape: no `messages`, just the turn count, so the sidebar stays light. */
export interface RagConversationSummary {
  id: number;
  title: string | null;
  agent_id: number;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface RagConversation {
  id: number;
  title: string | null;
  agent_id: number;
  organization_id: number;
  messages: RagConversationMessage[];
  created_at: string;
  updated_at: string;
}

/**
 * Uses fetch rather than axios on purpose. The global axios response interceptor
 * (common/util/auth.ts) unwraps any body carrying a top-level `data` key — a
 * Strapi-v4 convenience — which would reduce `{ data, total }` to just the array
 * and silently drop `total`, the number the sidebar's "has more" check depends
 * on. Scenar IA's paginated conversation list bypasses the interceptor the same
 * way, as does uploadDocument above.
 */
export async function fetchConversations(
  start = 0,
  limit = 20,
): Promise<{ data: RagConversationSummary[]; total: number }> {
  const jwt = localStorage.getItem("wixar.auth.jwt");
  const response = await fetch(`${apiUrls.rag.conversations}?start=${start}&limit=${limit}`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch conversations: ${response.status}`);
  }
  const body = await response.json();
  return { data: body.data ?? [], total: body.total ?? 0 };
}

/**
 * Safe to use axios: a single conversation has no top-level `data` key, so the
 * unwrapping interceptor described above leaves it alone.
 */
export async function fetchConversation(conversationId: number): Promise<RagConversation> {
  const response = await axios.get(`${apiUrls.rag.conversations}/${conversationId}`);
  return response.data;
}

export async function deleteConversation(conversationId: number): Promise<void> {
  await axios.delete(`${apiUrls.rag.conversations}/${conversationId}`);
}

// --- RAG Chat (streaming) ---

export async function askAgent(
  agentId: number,
  question: string,
  onChunk: (text: string) => void,
  onSources: (sources: RagSource[]) => void,
  onError: (error: string) => void,
  options: {
    /** Append to this thread; omit to open a new one. */
    conversationId?: number | null;
    /** Fires with the thread the backend filed this exchange under. */
    onConversationId?: (conversationId: number) => void;
  } = {},
): Promise<void> {
  const jwt = localStorage.getItem("wixar.auth.jwt");
  const { conversationId, onConversationId } = options;

  const response = await fetch(`${apiUrls.rag.agents}/${agentId}/ask`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({
      data: conversationId ? { question, conversationId } : { question },
    }),
  });

  if (response.status === 429) {
    onError("Trop de requêtes. Veuillez patienter quelques secondes.");
    return;
  }

  if (!response.ok) {
    onError(`Request failed: ${response.statusText}`);
    return;
  }

  // Read before draining the body: this is how a brand-new thread learns its id,
  // and the sidebar needs it whether or not the stream then succeeds. Only
  // readable because the backend CORS config exposes the header.
  const conversationIdHeader = response.headers.get("X-Conversation-Id");
  if (conversationIdHeader && onConversationId) {
    const parsed = Number(conversationIdHeader);
    if (Number.isFinite(parsed)) {
      onConversationId(parsed);
    }
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onError("No response stream");
    return;
  }

  const decoder = new TextDecoder();
  let fullText = "";

  let reading = true;
  while (reading) {
    const { done, value } = await reader.read();
    if (done) {
      reading = false;
      break;
    }

    const chunk = decoder.decode(value, { stream: true });
    fullText += chunk;

    // Check if we've received the sources delimiter
    const sourcesIdx = fullText.indexOf("\n__SOURCES__\n");
    if (sourcesIdx !== -1) {
      // Everything before the delimiter is the answer text
      const answerPart = fullText.substring(0, sourcesIdx);
      const sourcesPart = fullText.substring(sourcesIdx + "\n__SOURCES__\n".length);

      // Send any remaining answer text
      const alreadySent = fullText.length - chunk.length;
      if (alreadySent < sourcesIdx) {
        onChunk(answerPart.substring(Math.max(0, alreadySent)));
      }

      try {
        const parsed = JSON.parse(sourcesPart);
        onSources(parsed.sources || []);
      } catch {
        // Sources JSON might be incomplete, will be parsed when stream ends
      }
    } else {
      // No delimiter yet, all text is answer
      onChunk(chunk);
    }
  }

  // Final check for sources if not already parsed
  const sourcesIdx = fullText.indexOf("\n__SOURCES__\n");
  if (sourcesIdx !== -1) {
    const sourcesPart = fullText.substring(sourcesIdx + "\n__SOURCES__\n".length);
    try {
      const parsed = JSON.parse(sourcesPart);
      onSources(parsed.sources || []);
    } catch {
      // Invalid sources JSON
    }
  }

  // Check for error delimiter
  const errorIdx = fullText.indexOf("\n__ERROR__\n");
  if (errorIdx !== -1) {
    const errorPart = fullText.substring(errorIdx + "\n__ERROR__\n".length);
    try {
      const parsed = JSON.parse(errorPart);
      onError(parsed.error || "An error occurred");
    } catch {
      onError("An error occurred");
    }
  }
}
